"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, TrendingUp, Target, ShieldCheck, ArrowRight, CircleCheck as CheckCircle2, Map, Users } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const benefits = [{
  id: "benefit-1",
  title: "Drive Foot Traffic & Inquiries",
  description: "Capture high-intent searches from nearby customers. Through strategic local search optimisation, I optimize your Google Business Profile and local listings so searchers turn into direct calls, store visits, and inquiries.",
  highlight: "Improved local search visibility",
  badgeText: "High Intent"
}, {
  id: "benefit-2",
  title: "Build Authority in Your Market",
  description: "Establish your business as the go-to regional leader. As your dedicated local search consultant, I build lasting authority through localized content, robust citations, and targeted geographic signals.",
  highlight: "Local search performance monitoring",
  badgeText: "Market Trust"
}, {
  id: "benefit-3",
  title: "Higher Local Conversion Rates",
  description: "Local searchers are ready to buy. By aligning your landing pages with local buyer intent, I eliminate friction and maximize lead conversions from local traffic.",
  highlight: "Tailored local SEO recommendations",
  badgeText: "Ready Buyers"
}, {
  id: "benefit-4",
  title: "Outrank Big National Chains",
  description: "National chains use generic templates. I deploy hyper-focused regional SEO strategies that outsmart corporate budgets and win prime real estate in local search results.",
  highlight: "Location-focused SEO strategy",
  badgeText: "Competitive Edge"
}];
const directBenefits = ["Custom Google Business Profile & local schema optimization", "Tailored regional keyword & hyper-local competitor analysis", "High-converting local landing page architecture & review signals", "Transparent monthly reporting on actual local inquiries"];
export default function LocalBenefits() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="localseobenefits" data-nav="light" className="relative bg-background py-20 lg:py-28 text-foreground overflow-hidden isolate">
      <div className="absolute top-0 right-1/4 -z-10 h-96 w-96 rounded-full bg-secondary/50 blur-3xl" />
      <div className="absolute bottom-10 left-10 -z-10 h-80 w-80 rounded-full bg-accent/10 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            <MapPin className="size-3.5 text-primary mr-1" />
            Local SEO Strategy
          </Badge>
          <h2 className="text-2xl sm:text-3xl font-bold font-serif text-foreground tracking-tight mb-4">
            Why Local SEO Matters for Your Business Growth
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Effective local search optimisation isn&apos;t just about ranking — it&apos;s about capturing ready-to-buy customers in your target territory. Targeted local search optimisation reinforces your geographic prominence and citation consistency across the <Link to="/UkCitiesAreasPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">UK cities and regions I cover</Link> with comprehensive <Link to="/SeoAuditsPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">SEO audits</Link> and robust <Link to="/TechnicalSeoPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">technical SEO</Link> foundations. Whether aligning with a broader <Link to="/NationalSeoPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">national SEO strategy</Link> or preparing your brand for modern <Link to="/AISearchOptimisationPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">AI search optimisation</Link>, I treat local search optimisation as an ongoing discipline that compounds your visibility and search equity over time.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-16">
          {benefits.map((item, i) => <motion.div key={item.id} variants={cardVariants} data-index={i}>
              <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-shadow duration-300 flex flex-col justify-between">
                <CardHeader>
                  <div className="flex items-center justify-between mb-3">
                    <div className="size-11 rounded-lg bg-secondary text-primary flex items-center justify-center">
                      {i === 0 && <MapPin className="size-5" />}
                      {i === 1 && <ShieldCheck className="size-5" />}
                      {i === 2 && <Target className="size-5" />}
                      {i === 3 && <TrendingUp className="size-5" />}
                    </div>
                    <Badge variant="outline" className="text-xs font-medium">
                      {item.badgeText}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl font-semibold font-serif text-foreground">
                    {item.title}
                  </CardTitle>
                  <CardDescription className="text-muted-foreground text-sm leading-relaxed mt-2">
                    {item.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="mt-4 pt-4 border-t border-border/60">
                    <span className="text-sm font-medium text-primary">
                      {item.highlight}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="rounded-2xl bg-card border border-border p-8 lg:p-10 shadow-sm relative overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-5 flex flex-col sm:flex-row items-center gap-6">
              <div className="relative shrink-0">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-sizes" alt="Yassine Essaadi - Freelance SEO Consultant" loading="lazy" className="size-24 sm:size-28 rounded-full object-cover border-2 border-primary shadow-xs" />
                <span className="absolute bottom-1 right-1 size-4 rounded-full bg-emerald-500 border-2 border-card" />
              </div>
              <div>
                <Badge variant="secondary" className="mb-2 text-xs">
                  <Users className="size-3 mr-1 text-primary" /> Direct Access
                </Badge>
                <h3 className="text-xl font-bold font-serif text-foreground">
                  You Work Directly With Me
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Direct strategy and dedicated personal attention from Yassine across every aspect of your local search growth.
                </p>
              </div>
            </div>

            <div className="lg:col-span-7 flex flex-col justify-between">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {directBenefits.map((point, idx) => <div key={idx} data-index={idx} className="flex items-start gap-2.5">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span className="text-sm text-foreground font-medium">
                      {point}
                    </span>
                  </div>)}
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <Button asChild size="lg" className="rounded-md font-medium">
                  <Link to="/ContactPage">
                    Book a Free SEO Consultation
                    <ArrowRight className="size-4 ml-1" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="rounded-md font-medium">
                  <Link to="/CaseStudiesPage">
                    <Map className="size-4 mr-1" />
                    Explore Case Studies
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}